import { useCallback, useEffect, useMemo, useState } from "react";
import { LogOut, X, Mail, Globe, Clock, Plus, Pencil } from "lucide-react";
import { useMeta } from "../hooks/useMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = "sales_token";
export const STAGES = [
  { key: "new", label: "New" },
  { key: "call-booked", label: "Discovery call booked" },
  { key: "call-done", label: "Discovery call done" },
  { key: "plan-sent", label: "90-day plan sent" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];
const SERVICE_ORDER = ["Paid Traffic", "Ad Creative", "Retention (Email and SMS)", "AEO"];
const REVENUE = ["Under $150,000/month", "$150,000 to $250,000/month", "$250,000 to $500,000/month", "$500,000 to $1M/month", "$1M to $5M/month", "$5M to $10M/month", "$10M+/month"];
const SPEND = ["Under $30k/month", "$30k to $100k/month", "$100k to $500k/month", "$500k to $1M/month", "$1M to $5M/month", "$5M to $10M/month", "$10M+/month"];

const api = async (path, opts = {}) => {
  const res = await fetch(`${API}/api/sales${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(KEY) || ""}`, ...(opts.headers || {}) },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Request failed");
  return res.json();
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const deriveName = (d) => d.company || d.name || "Unknown";

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/api/sales/login`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }) });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      localStorage.setItem(KEY, data.token);
      onLogin();
    } catch (err) {
      setError(err.message);
    }
  };
  return (
    <main data-testid="sales-login" className="flex min-h-screen items-center justify-center bg-navy px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8">
        <img src="/assets/logos/logo-dark.png" alt="Smart Marketer Agency" className="h-7 w-auto" />
        <h1 className="mt-6 text-2xl font-bold text-navy">Sales</h1>
        <p className="mt-1 text-sm text-navy/60">Team password to continue.</p>
        <input data-testid="sales-password" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} className="mt-6 w-full rounded-lg border border-mist px-4 py-3 text-navy outline-none focus:border-navy" placeholder="Password" />
        {error && <p data-testid="sales-login-error" className="mt-3 text-sm font-bold text-pink">{error}</p>}
        <button data-testid="sales-login-submit" className="mt-5 w-full rounded-full bg-pink py-3 font-bold text-white hover:bg-pink-dark">Sign in</button>
      </form>
    </main>
  );
}

function DealCard({ deal, onOpen, onDragStart }) {
  return (
    <button
      draggable
      onDragStart={(e) => { onDragStart(deal); e.dataTransfer.effectAllowed = "move"; }}
      onClick={() => onOpen(deal)}
      data-testid={`deal-card-${deal.id}`}
      className="w-full cursor-grab rounded-xl border border-mist bg-white p-4 text-left shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-bold leading-tight text-navy">{deriveName(deal)}</div>
        <div className="flex shrink-0 gap-1">
          {deal.source === "application" && (
            <span data-testid={`deal-qualified-${deal.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${deal.qualified ? "bg-green/15 text-green" : "bg-pink/10 text-pink"}`}>{deal.qualified ? "Qualified" : "Below"}</span>
          )}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${deal.source === "application" ? "bg-navy text-white" : "bg-fog text-navy/60"}`}>{deal.source === "application" ? (deal.manual ? "Manual" : "Applied") : "Contact"}</span>
        </div>
      </div>
      <div className="mt-1 text-xs text-navy/60">{deal.name}{deal.ad_spend ? ` · ${deal.ad_spend}` : ""}</div>
      {deal.services && deal.services.length > 0 && <div className="mt-2 text-xs text-navy/70">{deal.services.join(", ")}</div>}
      {(deal.plan_url || deal.onboarded_at) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {deal.plan_url && <a href={deal.plan_url} target="_blank" rel="noopener noreferrer" data-testid={`deal-plan-link-${deal.id}`} onClick={(e) => e.stopPropagation()} className="rounded-full bg-fog px-2 py-0.5 text-[10px] font-bold text-link hover:underline">90-day plan</a>}
          {deal.onboarded_at && <span data-testid={`deal-onboarded-${deal.id}`} className="rounded-full bg-green/15 px-2 py-0.5 text-[10px] font-bold text-green">Onboarding sent</span>}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between text-[11px] text-navy/50">
        <span>{fmtDate(deal.created_at)}{deal.booked_at ? ` · booked ${fmtDate(deal.booked_at)}` : deal.source === "application" && deal.qualified && deal.stage === "new" ? " · not booked yet" : ""}</span>
        {deal.owner && <span className="font-bold text-navy/70">{deal.owner}</span>}
      </div>
      {deal.next_step && <div className="mt-2 flex items-center gap-1 text-[11px] font-medium text-pink"><Clock size={12} /> {deal.next_step}</div>}
    </button>
  );
}

function Drawer({ deal, onClose, onUpdate, onWon, onEdit }) {
  const [note, setNote] = useState("");
  const [author, setAuthor] = useState(localStorage.getItem("sales_author") || "");
  const [saving, setSaving] = useState(false);
  const patch = async (changes) => {
    setSaving(true);
    try { onUpdate(await api(`/deals/${deal.source}/${deal.id}`, { method: "PATCH", body: JSON.stringify(changes) })); } finally { setSaving(false); }
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    localStorage.setItem("sales_author", author);
    const n = await api(`/deals/${deal.source}/${deal.id}/notes`, { method: "POST", body: JSON.stringify({ text: note, author }) });
    onUpdate({ ...deal, notes: [...(deal.notes || []), n] });
    setNote("");
  };
  const [onboarding, setOnboarding] = useState("");
  const sendOnboarding = async () => {
    setOnboarding("sending");
    try {
      const r = await api(`/deals/${deal.source}/${deal.id}/onboarding`, { method: "POST" });
      onUpdate({ ...deal, onboarded_at: r.onboarded_at });
      setOnboarding("sent");
    } catch {
      setOnboarding("error");
    }
  };
  const field = "mt-1 w-full rounded-lg border border-mist px-3 py-2 text-sm text-navy outline-none focus:border-navy";
  return (
    <div data-testid="deal-drawer" className="fixed inset-0 z-50 flex justify-end bg-navy/60" onClick={onClose}>
      <aside className="h-full w-full max-w-xl overflow-y-auto bg-white p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-navy">{deriveName(deal)}</h2>
            <div className="mt-1 text-sm text-navy/60">{deal.name} · {fmtDate(deal.created_at)} · from {deal.ref || "/"}</div>
          </div>
          <div className="flex items-center gap-1">
            <button data-testid="deal-edit" type="button" onClick={() => onEdit(deal)} className="inline-flex items-center gap-1 rounded-full border border-mist px-3 py-1.5 text-xs font-bold text-navy hover:border-navy"><Pencil size={12} /> Edit</button>
            <button data-testid="deal-drawer-close" onClick={onClose} aria-label="Close" className="rounded-full p-2 hover:bg-fog"><X size={22} /></button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          {deal.email && <a href={`mailto:${deal.email}`} className="inline-flex items-center gap-1 font-bold text-link"><Mail size={14} /> {deal.email}</a>}
          {deal.website && <a href={deal.website.startsWith("http") ? deal.website : `https://${deal.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-link"><Globe size={14} /> {deal.website}</a>}
        </div>

        {deal.source === "application" && (
          <dl className="mt-6 grid grid-cols-2 gap-4 rounded-xl bg-fog p-5 text-sm">
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Revenue</dt><dd className="mt-1 text-navy">{deal.revenue}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Ad spend</dt><dd className="mt-1 text-navy">{deal.ad_spend}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Services</dt><dd className="mt-1 text-navy">{(deal.services || []).join(", ") || "n/a"}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Paid channels</dt><dd className="mt-1 text-navy">{(deal.channels || []).join(", ") || "n/a"}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Qualified</dt><dd data-testid="deal-drawer-qualified" className={`mt-1 font-bold ${deal.qualified ? "text-green" : "text-pink"}`}>{deal.qualified ? "Yes ($30k+ spend or $150k+ revenue)" : "No, below threshold"}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Call booked</dt><dd className="mt-1 text-navy">{deal.booked_at ? new Date(deal.booked_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : deal.qualified ? "Not yet" : "n/a"}</dd></div>
            {deal.heard_from && <div className="col-span-2"><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Heard from</dt><dd className="mt-1 text-navy">{deal.heard_from}</dd></div>}
          </dl>
        )}
        <div className="mt-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-navy/50">{deal.source === "application" ? "What they want to fix" : "Message"}</div>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-navy">{deal.message}</p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <label className="block text-xs font-bold uppercase tracking-widest text-navy/60">Stage
            <select data-testid="deal-stage" value={deal.stage} onChange={(e) => (e.target.value === "won" && deal.stage !== "won" ? onWon(deal) : patch({ stage: e.target.value }))} className={field}>
              {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
          <label className="block text-xs font-bold uppercase tracking-widest text-navy/60">Owner
            <input data-testid="deal-owner" defaultValue={deal.owner} onBlur={(e) => e.target.value !== deal.owner && patch({ owner: e.target.value })} className={field} placeholder="Who is on it" />
          </label>
          <label className="block text-xs font-bold uppercase tracking-widest text-navy/60 sm:col-span-2">Next step
            <input data-testid="deal-next-step" defaultValue={deal.next_step} onBlur={(e) => e.target.value !== deal.next_step && patch({ next_step: e.target.value })} className={field} placeholder="e.g. Call Thu 3pm, send plan by Friday" />
          </label>
          <label className="block text-xs font-bold uppercase tracking-widest text-navy/60 sm:col-span-2">90-day plan link
            <input data-testid="deal-plan-url" type="url" defaultValue={deal.plan_url} onBlur={(e) => e.target.value !== deal.plan_url && patch({ plan_url: e.target.value })} className={field} placeholder="https://plans.smartmarketeragency.com/brand" />
          </label>
          {deal.stage === "lost" && (
            <label className="block text-xs font-bold uppercase tracking-widest text-navy/60 sm:col-span-2">Lost reason
              <input data-testid="deal-lost-reason" defaultValue={deal.lost_reason} onBlur={(e) => e.target.value !== deal.lost_reason && patch({ lost_reason: e.target.value })} className={field} placeholder="Why did we lose it?" />
            </label>
          )}
        </div>
        {saving && <div className="mt-2 text-xs text-navy/50">Saving...</div>}

        {deal.won && (
          <dl data-testid="deal-won-details" className="mt-6 grid grid-cols-2 gap-4 rounded-xl border border-mist p-5 text-sm">
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Onboarding date</dt><dd className="mt-1 font-bold text-navy">{deal.won.onboarding_date || "n/a"}</dd></div>
            <div><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Our team</dt><dd className="mt-1 text-navy">{deal.won.team || "n/a"}</dd></div>
            <div className="col-span-2"><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Services we will run</dt><dd className="mt-1 text-navy">{(deal.won.services || []).join(", ") || "n/a"}</dd></div>
            <div className="col-span-2"><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Client points of contact</dt><dd className="mt-1 whitespace-pre-wrap text-navy">{deal.won.points_of_contact || "n/a"}</dd></div>
            {deal.won.notes && <div className="col-span-2"><dt className="text-[10px] font-bold uppercase tracking-widest text-navy/50">Handover notes</dt><dd className="mt-1 whitespace-pre-wrap text-navy">{deal.won.notes}</dd></div>}
            <div className="col-span-2"><button type="button" data-testid="deal-won-edit" onClick={() => onWon(deal)} className="text-xs font-bold text-link hover:underline">Edit onboarding details</button></div>
          </dl>
        )}
        {(deal.stage === "won" || deal.onboarded_at) && (
          <div data-testid="deal-onboarding" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-green/30 bg-green/5 p-4 text-sm">
            <div>
              <div className="font-bold text-navy">Client onboarding channel</div>
              <div className="mt-0.5 text-xs text-navy/60">{deal.onboarded_at ? `Sent ${new Date(deal.onboarded_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "Not sent yet"}{onboarding === "error" ? " · Slack did not accept it, try again" : ""}</div>
            </div>
            <button data-testid="deal-onboarding-send" type="button" onClick={sendOnboarding} disabled={onboarding === "sending"} className="rounded-full bg-green px-4 py-1.5 text-xs font-bold text-white hover:bg-green/90 disabled:opacity-60">
              {onboarding === "sending" ? "Sending..." : deal.onboarded_at ? "Send update to onboarding" : "Send to onboarding"}
            </button>
          </div>
        )}

        <div className="mt-8">
          <h3 className="text-sm font-bold text-navy">Notes</h3>
          <ul className="mt-3 space-y-3">
            {(deal.notes || []).slice().reverse().map((n) => (
              <li key={n.id} data-testid="deal-note" className="rounded-lg bg-fog p-3 text-sm text-navy">
                <div className="whitespace-pre-wrap">{n.text}</div>
                <div className="mt-1 text-[11px] text-navy/50">{n.author ? `${n.author} · ` : ""}{new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
              </li>
            ))}
            {(!deal.notes || deal.notes.length === 0) && <li className="text-sm text-navy/50">No notes yet.</li>}
          </ul>
          <form onSubmit={addNote} className="mt-4 space-y-2">
            <textarea data-testid="deal-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={field} placeholder="Add a note" />
            <div className="flex gap-2">
              <input data-testid="deal-note-author" value={author} onChange={(e) => setAuthor(e.target.value)} className={`${field} !mt-0 flex-1`} placeholder="Your name" />
              <button data-testid="deal-note-submit" className="rounded-full bg-navy px-5 text-sm font-bold text-white hover:bg-navy/90">Add note</button>
            </div>
          </form>
        </div>
      </aside>
    </div>
  );
}

function WonModal({ deal, onCancel, onSave }) {
  const w = deal.won || {};
  const [form, setForm] = useState({
    onboarding_date: w.onboarding_date || "",
    points_of_contact: w.points_of_contact || `${deal.name || ""}${deal.email ? ` (${deal.email})` : ""}`,
    services: w.services && w.services.length ? w.services : deal.services || [],
    team: w.team || "",
    notes: w.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (svc) => setForm((f) => ({ ...f, services: f.services.includes(svc) ? f.services.filter((x) => x !== svc) : [...f.services, svc] }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };
  const field = "mt-1 w-full rounded-lg border border-mist px-3 py-2 text-sm text-navy outline-none focus:border-navy";
  const label = "block text-xs font-bold uppercase tracking-widest text-navy/60";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4" onClick={onCancel}>
      <form data-testid="won-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-2xl">
        <div className="text-xs font-extrabold uppercase tracking-widest text-green">{deal.won ? "Edit onboarding details" : "Deal won"}</div>
        <h2 className="mt-2 text-2xl font-bold text-navy">{deriveName(deal)}</h2>
        <p className="mt-1 text-sm text-navy/60">These details go to the client onboarding channel in Slack.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className={label}>Onboarding date
            <input data-testid="won-onboarding-date" type="date" required value={form.onboarding_date} onChange={set("onboarding_date")} className={field} />
          </label>
          <label className={label}>Our team on the account
            <input data-testid="won-team" required value={form.team} onChange={set("team")} className={field} placeholder="e.g. Dilyana (paid), Ben (creative), Maria (CS)" />
          </label>
          <label className={`${label} sm:col-span-2`}>Client points of contact
            <textarea data-testid="won-contacts" required rows={2} value={form.points_of_contact} onChange={set("points_of_contact")} className={field} placeholder="Name, role, email. One per line." />
          </label>
          <div className="sm:col-span-2">
            <div className={label}>Services we will run</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {SERVICE_ORDER.map((svc) => {
                const on = form.services.includes(svc);
                return (
                  <button key={svc} type="button" data-testid={`won-service-${svc.split(" ")[0].toLowerCase()}`} aria-pressed={on} onClick={() => toggle(svc)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-navy bg-navy text-white" : "border-mist bg-white text-navy hover:border-navy"}`}>{svc}</button>
                );
              })}
            </div>
          </div>
          <label className={`${label} sm:col-span-2`}>Handover notes (optional)
            <textarea data-testid="won-notes" rows={3} value={form.notes} onChange={set("notes")} className={field} placeholder="Anything the onboarding team should know" />
          </label>
        </div>
        <div className="mt-8 flex items-center justify-end gap-3">
          <button type="button" data-testid="won-cancel" onClick={onCancel} className="text-sm font-bold text-navy/60 hover:text-navy">Cancel</button>
          <button type="submit" data-testid="won-submit" disabled={saving || form.services.length === 0} className="rounded-full bg-green px-6 py-2.5 text-sm font-bold text-white hover:bg-green/90 disabled:opacity-60">
            {saving ? "Saving..." : deal.won ? "Save and send update" : "Mark won and send to onboarding"}
          </button>
        </div>
      </form>
    </div>
  );
}

const CHANNELS = ["Meta", "Google", "TikTok", "AppLovin", "ChatGPT (beta)", "Other"];

function NewDealModal({ stage, deal, onCancel, onCreate }) {
  const [form, setForm] = useState(deal
    ? { company: deal.company || "", name: deal.name || "", email: deal.email || "", website: deal.website || "", revenue: deal.revenue || "", ad_spend: deal.ad_spend || "", services: deal.services || [], channels: deal.channels || [], message: deal.message || "", owner: deal.owner || "" }
    : { company: "", name: "", email: "", website: "", revenue: "", ad_spend: "", services: [], channels: [], message: "", owner: localStorage.getItem("sales_author") || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleIn = (key) => (v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const toggle = toggleIn("services");
  const toggleChannel = toggleIn("channels");
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onCreate(deal ? form : { ...form, stage }); } finally { setSaving(false); }
  };
  const field = "mt-1 w-full rounded-lg border border-mist px-3 py-2 text-sm text-navy outline-none focus:border-navy";
  const label = "block text-xs font-bold uppercase tracking-widest text-navy/60";
  const stageLabel = STAGES.find((s) => s.key === stage)?.label || stage;
  const editing = Boolean(deal);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 p-4" onClick={onCancel}>
      <form data-testid="new-deal-modal" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-2xl">
        <div className="text-xs font-extrabold uppercase tracking-widest text-pink">{editing ? "Edit deal" : `New deal in ${stageLabel}`}</div>
        <h2 className="mt-2 text-2xl font-bold text-navy">{editing ? deriveName(deal) : "Add a lead by hand"}</h2>
        <p className="mt-1 text-sm text-navy/60">{editing ? "Changes save to the CRM only. Nothing is sent to Slack." : "For leads that came in by email, referral or a call. No Slack alert is sent."}</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className={label}>Brand
            <input data-testid="new-deal-company" required autoFocus value={form.company} onChange={set("company")} className={field} placeholder="Brand name" />
          </label>
          <label className={label}>Contact name
            <input data-testid="new-deal-name" required value={form.name} onChange={set("name")} className={field} placeholder="First and last name" />
          </label>
          <label className={label}>Email
            <input data-testid="new-deal-email" type="email" value={form.email} onChange={set("email")} className={field} placeholder="name@brand.com" />
          </label>
          <label className={label}>Website
            <input data-testid="new-deal-website" value={form.website} onChange={set("website")} className={field} placeholder="brand.com" />
          </label>
          <label className={label}>Monthly revenue
            <select data-testid="new-deal-revenue" value={form.revenue} onChange={set("revenue")} className={field}>
              <option value="">Unknown</option>
              {REVENUE.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className={label}>Monthly ad spend
            <select data-testid="new-deal-spend" value={form.ad_spend} onChange={set("ad_spend")} className={field}>
              <option value="">Unknown</option>
              {SPEND.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <div className="sm:col-span-2">
            <div className={label}>Services they are interested in</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {SERVICE_ORDER.map((svc) => {
                const on = form.services.includes(svc);
                return <button key={svc} type="button" data-testid={`new-deal-service-${svc.split(" ")[0].toLowerCase()}`} aria-pressed={on} onClick={() => toggle(svc)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-navy bg-navy text-white" : "border-mist bg-white text-navy hover:border-navy"}`}>{svc}</button>;
              })}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className={label}>Paid channels</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {CHANNELS.map((ch) => {
                const on = form.channels.includes(ch);
                return <button key={ch} type="button" data-testid={`new-deal-channel-${ch.split(" ")[0].toLowerCase()}`} aria-pressed={on} onClick={() => toggleChannel(ch)} className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${on ? "border-navy bg-navy text-white" : "border-mist bg-white text-navy hover:border-navy"}`}>{ch}</button>;
              })}
            </div>
          </div>
          <label className={`${label} sm:col-span-2`}>{editing ? "What they want to fix" : "Context"}
            <textarea data-testid="new-deal-message" rows={3} value={form.message} onChange={set("message")} className={field} placeholder="Where they came from, what they want to fix" />
          </label>
          <label className={label}>Owner
            <input data-testid="new-deal-owner" value={form.owner} onChange={set("owner")} className={field} placeholder="Who is on it" />
          </label>
        </div>
        <div className="mt-8 flex items-center justify-end gap-3">
          <button type="button" data-testid="new-deal-cancel" onClick={onCancel} className="text-sm font-bold text-navy/60 hover:text-navy">Cancel</button>
          <button type="submit" data-testid="new-deal-submit" disabled={saving} className="rounded-full bg-navy px-6 py-2.5 text-sm font-bold text-white hover:bg-navy/90 disabled:opacity-60">{saving ? "Saving..." : editing ? "Save changes" : "Add deal"}</button>
        </div>
      </form>
    </div>
  );
}

function Board({ onLogout }) {
  const [won, setWon] = useState(null);
  const [adding, setAdding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deals, setDeals] = useState(null);
  const [open, setOpen] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [q, setQ] = useState("");
  const [service, setService] = useState("");
  const [fit, setFit] = useState("");

  const load = useCallback(async () => {
    try { setDeals(await api("/deals")); } catch (e) { if (e.message === "unauthorized") onLogout(); }
  }, [onLogout]);
  useEffect(() => { load(); }, [load]);

  const update = (d) => { setDeals((ds) => ds.map((x) => (x.id === d.id ? d : x))); setOpen((o) => (o && o.id === d.id ? d : o)); };
  const moveTo = async (stage) => {
    setOverStage(null);
    if (!dragging || dragging.stage === stage) return;
    const d = dragging; setDragging(null);
    if (stage === "won") { setWon(d); return; }
    update({ ...d, stage });
    try { update(await api(`/deals/${d.source}/${d.id}`, { method: "PATCH", body: JSON.stringify({ stage }) })); } catch { load(); }
  };

  const services = useMemo(() => {
    const seen = new Set((deals || []).flatMap((d) => d.services || []));
    return [...SERVICE_ORDER.filter((s) => seen.has(s)), ...[...seen].filter((s) => !SERVICE_ORDER.includes(s)).sort()];
  }, [deals]);
  const filtered = useMemo(() => (deals || []).filter((d) =>
    (!q || `${deriveName(d)} ${d.name} ${d.email}`.toLowerCase().includes(q.toLowerCase())) &&
    (!service || (d.services || []).includes(service)) &&
    (!fit || (fit === "qualified" ? d.qualified === true : fit === "below" ? d.qualified === false : d.qualified === true && d.stage === "new"))
  ), [deals, q, service, fit]);

  return (
    <main data-testid="sales-board" className="flex h-screen flex-col bg-fog">
      <header className="flex items-center justify-between border-b border-mist bg-white px-5 py-3">
        <div className="flex items-center gap-4">
          <img src="/assets/logos/logo-dark.png" alt="Smart Marketer Agency" className="h-6 w-auto" />
          <span className="text-sm font-bold text-navy">Sales pipeline</span>
          {deals && <span data-testid="deal-count" className="text-xs text-navy/50">{filtered.length === deals.length ? `${deals.length} deals` : `${filtered.length} of ${deals.length} deals`}</span>}
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="sales-fit-filter" value={fit} onChange={(e) => setFit(e.target.value)} className={`rounded-full border px-4 py-1.5 text-sm outline-none focus:border-navy ${fit ? "border-navy bg-navy text-white" : "border-mist bg-white text-navy"}`}>
            <option value="">All leads</option>
            <option value="qualified">Qualified</option>
            <option value="not-booked">Qualified, not booked</option>
            <option value="below">Below threshold</option>
          </select>
          <select data-testid="sales-service-filter" value={service} onChange={(e) => setService(e.target.value)} className={`rounded-full border px-4 py-1.5 text-sm outline-none focus:border-navy ${service ? "border-navy bg-navy text-white" : "border-mist bg-white text-navy"}`}>
            <option value="">All services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="sales-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="rounded-full border border-mist px-4 py-1.5 text-sm text-navy outline-none focus:border-navy" />
          <button data-testid="sales-logout" onClick={onLogout} className="inline-flex items-center gap-1 text-sm text-navy/60 hover:text-navy"><LogOut size={16} /> Sign out</button>
        </div>
      </header>
      <div className="flex flex-1 gap-4 overflow-x-auto p-5">
        {STAGES.map((s) => {
          const col = filtered.filter((d) => d.stage === s.key);
          return (
            <section
              key={s.key}
              data-testid={`stage-${s.key}`}
              onDragOver={(e) => { e.preventDefault(); setOverStage(s.key); }}
              onDragLeave={() => setOverStage(null)}
              onDrop={() => moveTo(s.key)}
              className={`flex w-72 shrink-0 flex-col rounded-2xl border transition-colors ${overStage === s.key ? "border-pink bg-pink/5" : "border-transparent bg-mist/40"}`}
            >
              <div className="flex items-center justify-between px-4 pt-4">
                <h2 className="text-xs font-extrabold uppercase tracking-widest text-navy">{s.label}</h2>
                <div className="flex items-center gap-1.5">
                  <span data-testid={`stage-count-${s.key}`} className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-navy/60">{col.length}</span>
                  <button type="button" data-testid={`stage-add-${s.key}`} aria-label={`Add deal to ${s.label}`} onClick={() => setAdding(s.key)} className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-navy/60 transition-colors hover:bg-navy hover:text-white"><Plus size={14} /></button>
                </div>
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto p-3">
                {col.map((d) => <DealCard key={d.id} deal={d} onOpen={setOpen} onDragStart={setDragging} />)}
              </div>
            </section>
          );
        })}
      </div>
      {open && <Drawer deal={open} onClose={() => setOpen(null)} onUpdate={update} onWon={setWon} onEdit={setEditing} />}
      {editing && (
        <NewDealModal
          deal={editing}
          onCancel={() => setEditing(null)}
          onCreate={async (form) => {
            try { update(await api(`/deals/${editing.source}/${editing.id}`, { method: "PATCH", body: JSON.stringify(form) })); } catch { load(); }
            setEditing(null);
          }}
        />
      )}
      {adding && (
        <NewDealModal
          stage={adding}
          onCancel={() => setAdding(null)}
          onCreate={async (form) => {
            const created = await api("/deals", { method: "POST", body: JSON.stringify(form) });
            if (form.owner) localStorage.setItem("sales_author", form.owner);
            setAdding(null);
            if (form.stage === "won") { setDeals((ds) => [created, ...ds]); setWon(created); return; }
            setDeals((ds) => [created, ...ds]);
            setOpen(created);
          }}
        />
      )}
      {won && (
        <WonModal
          deal={won}
          onCancel={() => setWon(null)}
          onSave={async (form) => {
            try {
              const updated = await api(`/deals/${won.source}/${won.id}`, { method: "PATCH", body: JSON.stringify({ stage: "won", won: form }) });
              if (won.won) await api(`/deals/${won.source}/${won.id}/onboarding`, { method: "POST" });
              update(updated);
            } catch { load(); }
            setWon(null);
          }}
        />
      )}
    </main>
  );
}

export default function Sales() {
  useMeta({ title: "Sales | Smart Marketer Agency", description: "Internal sales pipeline." });
  const [authed, setAuthed] = useState(null);
  const logout = useCallback(() => { localStorage.removeItem(KEY); setAuthed(false); }, []);
  useEffect(() => {
    api("/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);
  if (authed === null) return <div className="min-h-screen bg-navy" />;
  return authed ? <Board onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
